import { ChevronRightRounded, ExpandMoreRounded } from "@mui/icons-material";
import { SimpleTreeView as MuiTreeView } from "@mui/x-tree-view";
import { ITreeItem } from "../models";
import { TreeViewItem } from "./TreeViewItem";

interface IProps {
  items: ITreeItem[];
  selectedItemIds: string[];
  onItemSelected: (selectedItems: ITreeItem[]) => void;
}

const TreeView = (props: IProps) => {
  function getSelectedItemIdAndDescendantItemIds(itemId: string, items: ITreeItem[]): string[] {
    var selectedItem = items.find((x) => x.itemId === itemId);

    if (selectedItem) {
      return getDescendantTreeItems(selectedItem, items).map((x) => x.itemId);
    } else {
      return [];
    }
  }

  function getDescendantTreeItems(item: ITreeItem, items: ITreeItem[]): ITreeItem[] {
    var childItems = items.filter((x) => x.parentItemId === item.itemId);

    if (childItems?.length === 0) {
      return [item];
    } else {
      return [item, ...(childItems?.flatMap((x) => getDescendantTreeItems(x, items)) ?? [])];
    }
  }

  function renderDescendantTreeItems(item: ITreeItem, items: ITreeItem[], selectedItemIds: string[]) {
    const children = items.filter((x) => x.parentItemId === item.itemId);

    return (
      <TreeViewItem
        key={item.itemId}
        itemId={item.itemId}
        label={item.label}
        items={items}
        selectedItemIds={selectedItemIds}
        onItemSelected={(itemId) => {
          var selectedItemIdAndDescendantItemIds = getSelectedItemIdAndDescendantItemIds(itemId, items);

          if (selectedItemIds.includes(itemId)) {
            // Remove item and its descendants.
            const newSelectedItemIds = selectedItemIds.filter((x) => !selectedItemIdAndDescendantItemIds.includes(x));
            const result = items.filter((x) => newSelectedItemIds.includes(x.itemId));

            props.onItemSelected(result);
          } else {
            // Add item and its descendants
            const newSelectedItemIds = [...selectedItemIds, ...selectedItemIdAndDescendantItemIds];
            const result = items.filter((x) => newSelectedItemIds.includes(x.itemId));

            props.onItemSelected(result);
          }
        }}
      >
        {children.map((x) => renderDescendantTreeItems(x, items, selectedItemIds))}
      </TreeViewItem>
    );
  }

  return (
    <MuiTreeView
      multiSelect
      selectedItems={props.selectedItemIds}
      slotProps={{
        collapseIcon: <ExpandMoreRounded />,
        expandIcon: <ChevronRightRounded />,
      }}
      itemChildrenIndentation={20}
    >
      {props.items
        .filter((x) => x.parentItemId == null)
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((x) => renderDescendantTreeItems(x, props.items, props.selectedItemIds))}
    </MuiTreeView>
  );
};

function getTreeViewSelectedItemsLabel(selectedItems: ITreeItem[]) {
  let label = "-";
  let shallowestItem: ITreeItem | undefined = undefined;

  if (selectedItems.length === 1) {
    label = selectedItems[0].label;
  } else if (selectedItems.length > 1) {
    shallowestItem = selectedItems.sort((a, b) => a.depth - b.depth || a.label.localeCompare(b.label))[0];

    if (shallowestItem) {
      label = `${shallowestItem.label}... (${selectedItems.length} teams)`;
    }
  }

  return label;
}

export { getTreeViewSelectedItemsLabel, TreeView };
